import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, '')}${path}`
  }
  return path
}

export interface WorkflowTemplateSummaryDto {
  workflowId: string
  workflowVersionId: string
  templateId: string
  templateVersionId: string
  tenantId: string
  templateName: string
  permitCode: string
  siteName: string
  workflowPriority: number
  workflowIsActive: boolean
  workflowVersion: number
}

export interface WorkflowMetadataDto {
  workflowId: string
  workflowVersionId: string
  templateId: string
  templateVersionId: string
  tenantId: string
  workflowName: string
  workflowCode: string
  priority: number
  version: number
  isActive: boolean
  description?: string | null
  capStageId?: string | null
  resubmitPolicy: 'RESTART' | 'RETURNED'
  finalStatusCode?: string | null
}

export interface WorkflowStageConfigDto {
  stageId: string
  workflowVersionId: string
  code: string
  name: string
  seqOrder: number
  isGate: boolean
  onMissingApprover?: 'FAIL' | 'SKIP' | null
  roleId?: string | null
  roleName?: string | null
  fieldId?: string | null
  fieldName?: string | null
  statusCode?: string | null
  statusName?: string | null
}

export interface WorkflowRuleConditionDto {
  workflowRuleConditionId: string
  workflowRulesetId: string
  fieldId: string
  fieldCode?: string | null
  operator: string
  value?: string | null
}

export interface WorkflowRulesetConfigDto {
  workflowRulesetId: string
  workflowVersionId: string
  name: string
  priority: number
  capStageId: string
  excludeStageIds: string[]
  description?: string | null
  conditions: WorkflowRuleConditionDto[]
}

export interface WorkflowConfigurationDetailDto {
  metadata: WorkflowMetadataDto
  stages: WorkflowStageConfigDto[]
  rulesets: WorkflowRulesetConfigDto[]
}

export interface UpdateWorkflowConfigurationRequestDto {
  metadata: WorkflowMetadataDto
  stages: WorkflowStageConfigDto[]
  rulesets: WorkflowRulesetConfigDto[]
}

export interface PagedResultDto<T> {
  items: T[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export interface WorkflowEmployeeLookupDto {
  employeeId: string
  employeeCode?: string | null
  fullName: string
  position?: string | null
  orgName?: string | null
  companyId?: string | null
  companyName?: string | null
}

export interface WorkflowRoleLookupDto {
  roleId: string
  code: string
  displayName: string
  description?: string | null
}

export interface WorkflowTemplateFieldLookupDto {
  fieldId: string
  fieldCode: string
  fieldLabel: string
  sectionCode: string
  sectionTitle: string
}

export interface WorkflowTemplateVersionOptionDto {
  templateVersionId: string
  templateId: string
  tenantId: string
  templateName: string
  templateCode: string
  permitCode: string
  version: number
  status: string
}

export interface CreateWorkflowRequestDto {
  tenantId: string
  templateVersionId: string
  workflowName: string
  workflowCode: string
  priority: number
  isActive: boolean
  copyFromWorkflowVersionId?: string | null
  resubmitPolicy: 'RESTART' | 'RETURNED'
  finalStatusCode?: string | null
}

export async function getWorkflowTemplates(
  tenantId: string,
  permitCode?: string,
): Promise<WorkflowTemplateSummaryDto[]> {
  const response = await axios.get<WorkflowTemplateSummaryDto[]>(buildUrl('/api/v1/workflow-config/templates'), {
    params: { tenantId, permitCode },
  })
  return response.data ?? []
}

export async function getWorkflowTemplateVersions(
  tenantId: string,
): Promise<WorkflowTemplateVersionOptionDto[]> {
  const response = await axios.get<WorkflowTemplateVersionOptionDto[]>(
    buildUrl('/api/v1/workflow-config/template-versions'),
    {
      params: { tenantId },
    },
  )
  return response.data ?? []
}

export async function getWorkflowConfiguration(
  workflowVersionId: string,
): Promise<WorkflowConfigurationDetailDto> {
  const response = await axios.get<WorkflowConfigurationDetailDto>(
    buildUrl(`/api/v1/workflow-config/${workflowVersionId}`),
  )
  return response.data
}

export async function updateWorkflowConfiguration(
  workflowVersionId: string,
  payload: UpdateWorkflowConfigurationRequestDto,
): Promise<WorkflowConfigurationDetailDto> {
  const response = await axios.post<WorkflowConfigurationDetailDto>(
    buildUrl(`/api/v1/workflow-config/${workflowVersionId}`),
    payload,
  )
  return response.data
}

export async function createWorkflow(
  payload: CreateWorkflowRequestDto,
): Promise<WorkflowConfigurationDetailDto> {
  const response = await axios.post<WorkflowConfigurationDetailDto>(buildUrl('/api/v1/workflow-config'), payload)
  return response.data
}

export async function searchWorkflowEmployees(
  tenantId: string,
  search?: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<PagedResultDto<WorkflowEmployeeLookupDto>> {
  const response = await axios.get<PagedResultDto<WorkflowEmployeeLookupDto>>(
    buildUrl('/api/v1/workflow-config/employees'),
    {
      params: {
        tenantId,
        search,
        page: pageNumber,
        pageSize,
      },
    },
  )
  return response.data
}

export async function searchWorkflowRoles(
  search?: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<PagedResultDto<WorkflowRoleLookupDto>> {
  const response = await axios.get<PagedResultDto<WorkflowRoleLookupDto>>(buildUrl('/api/v1/workflow-config/roles'), {
    params: {
      search,
      page: pageNumber,
      pageSize,
    },
  })
  return response.data
}

export async function searchWorkflowTemplateFields(
  templateVersionId: string,
  search?: string,
  pageNumber = 1,
  pageSize = 20,
): Promise<PagedResultDto<WorkflowTemplateFieldLookupDto>> {
  const response = await axios.get<PagedResultDto<WorkflowTemplateFieldLookupDto>>(
    buildUrl('/api/v1/workflow-config/template-fields'),
    {
      params: {
        templateVersionId,
        search,
        page: pageNumber,
        pageSize,
      },
    },
  )
  return response.data
}

export async function getWorkflowStatuses(tenantId: string): Promise<WorkflowStatusOptionDto[]> {
  const response = await axios.get<WorkflowStatusOptionDto[]>(buildUrl('/api/v1/workflow-config/statuses'), {
    params: {
      tenantId,
    },
  })
  return response.data
}

export interface WorkflowListItemDto {
  workflowId: string
  workflowVersionId: string
  templateId: string
  templateVersionId: string
  tenantId: string
  workflowName: string
  workflowCode: string
  templateName: string
  permitCode: string
  siteName: string
  version: number
  priority: number
  isActive: boolean
  createdAt: string
  usageCount: number
  canDelete: boolean
  resubmitPolicy: 'RESTART' | 'RETURNED'
  finalStatusCode?: string | null
}

export interface WorkflowStatusUpdateRequestDto {
  isActive: boolean
}

export interface WorkflowStatusOptionDto {
  statusCode: string
  statusName: string
}

interface WorkflowSearchParams {
  tenantId: string
  search?: string
  status?: 'all' | 'active' | 'inactive'
  page?: number
  pageSize?: number
}

export async function searchWorkflows({
  tenantId,
  search,
  status = 'all',
  page = 1,
  pageSize = 20,
}: WorkflowSearchParams): Promise<PagedResultDto<WorkflowListItemDto>> {
  const response = await axios.get<PagedResultDto<WorkflowListItemDto>>(buildUrl('/api/v1/workflow-config/workflows'), {
    params: {
      tenantId,
      search,
      status,
      page,
      pageSize,
    },
  })
  return response.data
}

export async function updateWorkflowStatus(workflowVersionId: string, isActive: boolean): Promise<void> {
  await axios.patch(buildUrl(`/api/v1/workflow-config/${workflowVersionId}/status`), {
    isActive,
  } satisfies WorkflowStatusUpdateRequestDto)
}

export async function deleteWorkflowVersion(workflowVersionId: string): Promise<void> {
  await axios.delete(buildUrl(`/api/v1/workflow-config/${workflowVersionId}`))
}
